import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { postLogOut, getAccount, cancelProject } from "../../utils/api";
import { useAuth } from "../../hooks/useAuth";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserData } from "../../features/userSlice";
import useStore from "../../store";
import useHidePopUp from "../../hooks/useHidePopUp";

const AccountWidget = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [isNotificationVisited, setIsNotificationVisited] = useState(false);
  const { notifications, avatar, setAvatar, avatarUpdateTrigger } = useStore();
  const [isPathChanged, setIsPathChanged] = useState(false);

  const dispatch = useDispatch();
  const { userData, status } = useSelector((state) => state.user);
  const isLoading = status === "loading";
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [timestamp, setTimestamp] = useState(Date.now());

  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const defaultAvatar = `https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png`;
  const storedAvatar = localStorage.getItem("userAvatar") || defaultAvatar;

  const accountIconRef = useRef();
  const notificationsRef = useRef();
  // useHidePopUp(accountIconRef); // Not working yet
  // useHidePopUp(notificationsRef); // Not working yet

  // Fetch user data on component mount, when accessToken changes, or when avatar is updated
  useEffect(() => {
    // Skip API calls if we don't have an access token
    if (!accessToken) return;
    
    // Add a flag to track if the component is still mounted
    let isMounted = true;
    
    // Fetch user data from API
    getAccount(accessToken)
      .then((res) => {
        // Only update state if component is still mounted
        if (isMounted) {
          const userData = res.data.data;
          setAvatar(userData.avatar || defaultAvatar);
          localStorage.setItem("userAvatar", userData.avatar || defaultAvatar);
          
          // Force a timestamp update to ensure image refresh
          setTimestamp(Date.now());
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.error("Error fetching account data:", err);
        }
      });
      
    // Only dispatch fetchUserData if we need to update Redux state
    dispatch(fetchUserData(accessToken));
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [accessToken, dispatch, avatarUpdateTrigger]);

  // Update avatar loading state when userData changes
  useEffect(() => {
    if (userData?.avatarLoading) {
      setAvatarLoading(true);
    } else {
      setAvatarLoading(false);
      // Update timestamp to force image refresh
      setTimestamp(Date.now());
    }
  }, [userData]);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    setShowNotification(false);
  };

  const handleReturnToPreviousUser = () => {
    const previousUserData = JSON.parse(
      localStorage.getItem("previousUserData"),
    );
    if (previousUserData) {
      localStorage.setItem("userData", JSON.stringify(previousUserData));
      localStorage.removeItem("previousUserData");
      navigate("/dashboard");
      window.location.reload();
    }
  };

  const previousUserExists = localStorage.getItem("previousUserData");

  // Fallback to localStorage data if Redux state hasn't loaded yet
  let fallbackUserData = null;
  try {
    fallbackUserData = JSON.parse(localStorage.getItem("userData"));
  } catch (e) {
    console.error("Error parsing userData from localStorage", e);
  }

  // Calculate full name with proper fallback logic
  let fullName = "User";

  if (userData && userData.first_name) {
    // If we have userData from API with first_name, use that
    fullName =
      `${userData.first_name || ""} ${userData.last_name || ""}`.trim();
  } else if (fallbackUserData && fallbackUserData.name) {
    // Otherwise fall back to localStorage name
    fullName = fallbackUserData.name;
  }

  // Function to get avatar URL with cache-busting timestamp
  const getAvatarUrl = (url) => {
    if (!url) {
      return (
        fallbackUserData?.avatar ||
        "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"
      );
    }
    // Add a timestamp query parameter to bypass browser cache
    return `${url}?t=${timestamp}`;
  };

  // Debug logging to see what's happening
  console.log("AccountWidget userData:", userData);
  console.log("Avatar URL:", userData?.avatar);

  return (
    <div className="flex items-center justify-end gap-x-[30px]">
      {/* Notifications */}
      {/* <button
        type="button"
        className="inline-flex relative"
        onClick={() => {
          setShowDropdown(false);
          setShowNotification((prev) => !prev);
          setIsNotificationVisited(true);
        }}
      >
        {!isNotificationVisited && (
          <span className="absolute w-[7px] h-[7px] rounded-full bg-orange-300 top-[-2px] right-0" />
        )}
        <svg
          width="18"
          height="19"
          viewBox="0 0 18 19"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.76 10.4579L16.373 5.47514C15.9256 3.8696 14.9545 2.45963 13.614 1.46922C12.2734 0.478821 10.6404 -0.035196 8.97423 0.00883637C7.3081 0.0528687 5.70446 0.652425 4.4181 1.71225C3.13175 2.77208 2.23645 4.23138 1.87446 5.85831L0.802548 10.6788C0.661135 11.3152 0.664442 11.9752 0.812223 12.6102C0.960005 13.2452 1.24849 13.8389 1.65639 14.3475C2.06428 14.856 2.58118 15.2665 3.16893 15.5486C3.75667 15.8307 4.40027 15.9773 5.05221 15.9774H5.36888C5.62081 16.8486 6.14902 17.6144 6.87394 18.1594C7.59886 18.7044 8.48124 18.9991 9.38817 18.9991C10.2951 18.9991 11.1775 18.7044 11.9024 18.1594C12.6273 17.6144 13.1555 16.8486 13.4075 15.9774H13.5603C14.2315 15.9775 14.8937 15.8223 15.4951 15.5241C16.0965 15.2259 16.6208 14.7927 17.0271 14.2583C17.4333 13.7239 17.7105 13.1028 17.837 12.4435C17.9634 11.7843 17.9357 11.1047 17.7561 10.4579H17.76ZM15.1404 12.8202C14.9567 13.0642 14.7185 13.262 14.4449 13.3978C14.1712 13.5336 13.8697 13.6036 13.5642 13.6024H5.05221C4.7559 13.6023 4.46338 13.5357 4.19624 13.4075C3.9291 13.2793 3.69416 13.0927 3.50876 12.8616C3.32335 12.6304 3.19222 12.3606 3.12503 12.072C3.05784 11.7834 3.05631 11.4834 3.12055 11.1941L4.19246 6.36498C4.43861 5.25353 5.04948 4.25637 5.92783 3.53223C6.80619 2.80808 7.90154 2.39857 9.03953 2.3689C10.1775 2.33922 11.2927 2.69107 12.2076 3.36845C13.1225 4.04583 13.7846 5.00979 14.0883 6.10689L15.4721 11.0896C15.5556 11.384 15.5691 11.6938 15.5115 11.9943C15.4539 12.2948 15.3268 12.5776 15.1404 12.8202Z"
            fill="white"
          />
        </svg>
      </button> */}

      <div className="flex items-center gap-x-2.5">
        <div className="w-[40px] h-[40px] relative rounded-full overflow-hidden">
          {avatarLoading ? (
            <div className="absolute w-full h-full flex items-center justify-center bg-gray-800">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-orange-500"></div>
            </div>
          ) : (
            <img
              key={`avatar-${timestamp}`}
              src={`${avatar || defaultAvatar}?t=${timestamp}`}
              className="absolute w-full h-full object-cover object-center cursor-pointer"
              alt="Profile"
              onClick={() => {
                setShowDropdown(!showDropdown);
                setShowNotification(false);
              }}
            />
          )}
        </div>

        <button
          type="button"
          className="flex items-center gap-x-1.5"
          onClick={toggleDropdown}
        >
          <span className="text-white font-semibold text-[14px] leading-normal">
            {fullName}
          </span>
          <svg
            width="18"
            height="19"
            viewBox="0 0 18 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`${showDropdown ? "rotate-180" : ""}`}
          >
            <path
              d="M4.5 7.25L9 11.75L13.5 7.25"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* {showNotification && (
        <ul
          ref={notificationsRef}
          className="bg-black-100 absolute top-[calc(100%+1px)] rounded-lg right-6 max-w-64 min-h-14 flex flex-col gap-3 py-[22px] pl-[25px] pr-[22px]"
        >
          {notifications.map(({ notification, id, isRendered }) => (
            <li
              key={id}
              className={`text-white text-sm cursor-pointer overflow-hidden ${!isRendered ? "font-extrabold" : "font-medium"} visited:font-medium leading-normal capitalize relative after:absolute after:w-full after:bottom-[-4px] after:left-0 after:h-[1px] after:bg-white after:transition-transform after:ease-out after:duration-[250ms] after:origin-bottom-left after:scale-x-0`}
            >
              <Link to={`/notifications/${id}`}>
                {notification.slice(0, 50)}...
              </Link>
            </li>
          ))}
        </ul>
      )} */}

      {showDropdown && (
        <ul
          ref={accountIconRef}
          className="bg-black-100 absolute top-[calc(100%+1px)] right-0 w-52 h-auto flex flex-col gap-3 py-[22px] pl-[25px] pr-[22px]"
        >
          <li>
            <NavLink
              to="/settings"
              className="text-white text-[14px] leading-normal font-medium capitalize relative after:absolute after:w-full after:bottom-[-4px] after:left-0 after:h-[1px] after:bg-white after:transition-transform after:ease-out after:duration-[250ms] after:origin-bottom-left after:scale-x-0 hover:after:scale-x-100"
            >
              Account settings
            </NavLink>
          </li>
          <li>
            <button
              onClick={(e) => {
                e.preventDefault();
                // Call cancelProject before logging out
                if (accessToken) {
                  cancelProject(accessToken)
                    .catch((error) => {
                      console.error("Error canceling project during logout:", error);
                    })
                    .finally(() => {
                      // Proceed with logout regardless of cancelProject result
                      postLogOut(accessToken).then((res) => {
                        if (res.data.success) {
                          localStorage.clear();
                          // setAvatar(defaultAvatar)
                          navigate("/login");
                        }
                      });
                    });
                } else {
                  // If no access token, just proceed with logout
                  postLogOut(accessToken).then((res) => {
                    if (res.data.success) {
                      localStorage.clear();
                      navigate("/login");
                    }
                  });
                }
              }}
              className="text-white text-[14px] leading-normal font-medium capitalize relative after:absolute after:w-full after:bottom-[-4px] after:left-0 after:h-[1px] after:bg-white after:transition-transform after:ease-out after:duration-[250ms] after:origin-bottom-left after:scale-x-0 hover:after:scale-x-100"
            >
              Log out
            </button>
          </li>
        </ul>
      )}

      {previousUserExists && (
        <button
          onClick={handleReturnToPreviousUser}
          className="bg-orange-100 text-white px-4 py-2 rounded"
        >
          Login Back as superadmin
        </button>
      )}
    </div>
  );
};

export default AccountWidget;
